import { ParsedQuery } from "query-string"
import { ParsedUrlQuery } from "querystring"
import { SearchSortBy } from "../../../features/algolia/api/types/search"
import { parseQuery } from "../../../utils/url"
import { getDefaultSortBy } from "../data/sorting"
import { SearchState, SearchStatePrice } from "../types"

const parseParam = (value: string | string[] | undefined) =>
  Array.isArray(value) ? (value as string[]) : [value as string]

const parseParams = (hash: string) =>
  hash && hash.length > 0 ? parseQuery(hash) : undefined

const parseSwitch = (parsedParams: ParsedQuery<string> | undefined) => {
  if (!parsedParams?.switch) {
    return undefined
  }
  const map = new Map<string, string>()
  for (const code of parseParam(parsedParams.switch)) {
    map.set(code, code)
  }
  return map
}

const parseBrandNames = (parsedParams: ParsedQuery<string> | undefined) => {
  if (!parsedParams?.brand || parsedParams.brand.length <= 0) {
    return undefined
  }

  return parseParam(parsedParams.brand).map((x) => `brandName:${x}`)
}

const parseDiscount = (parsedParams: ParsedQuery<string> | undefined) => {
  if (!parsedParams?.discount || parsedParams.discount.length <= 0) {
    return undefined
  }

  return parseParam(parsedParams.discount).map((x) => parseInt(x))
}

const parseCategoryCodes = (parsedParams: ParsedQuery<string> | undefined) =>
  parsedParams?.category ? parseParam(parsedParams.category) : undefined

const parsePrice = (
  parsedParams: ParsedQuery<string> | undefined
): SearchStatePrice | undefined => {
  if (!parsedParams?.maxPrice) {
    return undefined
  }

  return {
    minPrice: parsedParams.minPrice
      ? parseInt(parsedParams.minPrice as string)
      : 0,
    maxPrice: parsedParams.maxPrice
      ? parseInt(parsedParams.maxPrice as string)
      : 1000,
  }
}

const parseFilters = (parsedParams: ParsedQuery<string> | undefined) => {
  if (!parsedParams?.tag) {
    return undefined
  }
  const tags = parseParam(parsedParams.tag)
  const filters = new Map<string, string[]>()
  for (const tag of tags) {
    const key = tag.split(":")[0]
    if (!filters.has(key)) {
      filters.set(key, [])
    }
    filters.get(key)?.push(tag.split(":")[1])
  }
  return filters
}

export const parseHashParams = (): SearchState => {
  const parsedHash = parseParams(window.location.hash)
  return {
    page: parsedHash?.page ? parseInt(parsedHash.page as string) : 1,
    orderBy: (parsedHash?.orderBy as SearchSortBy) ?? getDefaultSortBy(),
    switch: parseSwitch(parsedHash),
    categoryCodes: parseCategoryCodes(parsedHash),
    brandNames: parseBrandNames(parsedHash),
    discountPercentValues: parseDiscount(parsedHash),
    price: parsePrice(parsedHash),
    filters: parseFilters(parsedHash),
  }
}

export interface SearchOptions {
  showEanCode: boolean
  includeUnavailable: boolean
}

export const parseSearchOptions = (
  queryParams: ParsedUrlQuery
): SearchOptions => {
  return {
    includeUnavailable: queryParams.includeUnavailable === "true",
    showEanCode: queryParams.showEan === "true",
  }
}
